import { PageContainer, PageSectionTitle } from 'components/Layout/PageContainer';
import { CategoryTable } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/CategoryTable';
import { FormattedMessage } from 'react-intl';
import { ProductTable } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/tables/ProductTable';
import {
  useDemandHomeKpiData,
  useMerchantDemandReportData,
} from 'pages/insights-pro/performance-tab/reports/demand-monitoring/queryHooks';
import { DemandTrendFilterCard } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandTrendFilterCard';
import { useState } from 'react';
import { DemandTableRow } from 'pages/insights-pro/performance-tab/reports/demand-monitoring/DemandTableHelpers';

type FilterState = 'positive' | 'negative' | 'none';
type DemandFilters = 'positive' | 'negative';

export const DEMAND_MONITORING_ROOT = '/insights/performance/reports/demand-monitoring';

export function DemandHome() {
  const { data: merchantData } = useMerchantDemandReportData();

  const { data: kpis } = useDemandHomeKpiData();

  const [categoryFilterState, setCategoryFilterState] = useState<FilterState>('none');
  const [productFilterState, setProductFilterState] = useState<FilterState>('none');

  const toggleCategoryFilterState = (filter: DemandFilters) => {
    if (filter === 'positive') {
      setCategoryFilterState(categoryFilterState === 'positive' ? 'none' : 'positive');
    } else {
      setCategoryFilterState(categoryFilterState === 'negative' ? 'none' : 'negative');
    }
  };

  const toggleProductFilterState = (filter: DemandFilters) => {
    if (filter === 'positive') {
      setProductFilterState(productFilterState === 'positive' ? 'none' : 'positive');
    } else {
      setProductFilterState(productFilterState === 'negative' ? 'none' : 'negative');
    }
  };

  const getLocalFilterFunction = (filterState: FilterState): ((row: DemandTableRow) => boolean) | undefined => {
    if (filterState === 'none') return undefined;
    if (filterState === 'positive') {
      return (row: DemandTableRow) => {
        return row.ordQtyWow > 0;
      };
    } else if (filterState === 'negative') {
      return (row: DemandTableRow) => {
        return row.ordQtyWow < 0;
      };
    }
  };

  return (
    <PageContainer>
      <PageSectionTitle additionalTitleId="demand.daterange.last.seven.days">
        <FormattedMessage id="demand.category.title" />
      </PageSectionTitle>
      <div className="grid grid-cols-2 xl:grid-cols-2 gap-4 w-full xl:col-span-12 col-span-6">
        {kpis && (
          <DemandTrendFilterCard
            attribute={'category'}
            direction={'increase'}
            values={kpis?.categories}
            selected={categoryFilterState === 'positive'}
            onClick={() => toggleCategoryFilterState('positive')}
          />
        )}
        {kpis && (
          <DemandTrendFilterCard
            attribute={'category'}
            direction={'decrease'}
            values={kpis?.categories}
            selected={categoryFilterState === 'negative'}
            onClick={() => toggleCategoryFilterState('negative')}
          />
        )}
      </div>
      <CategoryTable
        merchantData={merchantData ?? undefined}
        localFilterFunction={getLocalFilterFunction(categoryFilterState)}
      />
      <PageSectionTitle>
        <FormattedMessage id="demand.product.title" />
      </PageSectionTitle>
      <div className="grid grid-cols-2 xl:grid-cols-2 gap-4 w-full xl:col-span-12 col-span-6">
        {kpis && (
          <DemandTrendFilterCard
            attribute={'product'}
            direction={'increase'}
            values={kpis?.products}
            selected={productFilterState === 'positive'}
            onClick={() => toggleProductFilterState('positive')}
          />
        )}
        {kpis && (
          <DemandTrendFilterCard
            attribute={'product'}
            direction={'decrease'}
            values={kpis?.products}
            selected={productFilterState === 'negative'}
            onClick={() => toggleProductFilterState('negative')}
          />
        )}
      </div>
      <ProductTable
        merchantData={merchantData ?? undefined}
        localFilterFunction={getLocalFilterFunction(productFilterState)}
      />
    </PageContainer>
  );
}
